// src/pages/CaseDetail.jsx
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, getTokens } from '../lib/api.js';
import PriorityBadge from '../components/PriorityBadge.jsx';

const NEXT_STATUS_OPTIONS = {
  active: ['follow_up', 'resolved'],
  follow_up: ['active', 'resolved'],
  resolved: ['closed'],
  assigned: ['active'],
  escalated: [],
};

const VOICE_NOTE_RE = /\[Attachment\] (.+?) ::file:([a-f0-9-]+)/;

function MessageContent({ caseId, content }) {
  const match = content.match(VOICE_NOTE_RE);
  if (!match) return content;
  const [, filename, fileId] = match;
  if (!/\.(webm|m4a|mp3|ogg|wav)$/i.test(filename)) return content;
  const { accessToken } = getTokens();
  return (
    <div>
      <div className="text-xs opacity-70 mb-1">🎤 Voice message</div>
      <audio controls src={`/api/cases/${caseId}/attachments/${fileId}?access_token=${encodeURIComponent(accessToken)}`} className="max-w-full" />
    </div>
  );
}

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [userTyping, setUserTyping] = useState(false);
  const typingTimeout = useRef(null);
  const userTypingTimeout = useRef(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const body = await api(`/cases/${id}`);
      setData(body.case);
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Tell the person's chat a staff member has seen their messages —
  // drives the "Seen" tick on their side. Re-fires whenever new activity
  // arrives (below), not just once on page load.
  const markSeen = useCallback(() => {
    api(`/realtime/cases/${id}/seen`, { method: 'POST' }).catch(() => {});
  }, [id]);

  useEffect(() => { markSeen(); }, [markSeen]);

  // Live channel — the actual gap this fixes: without this, a new message
  // from the person on the public chat never appeared here until the page
  // was manually reloaded. Reconnects every 10 minutes with a fresh token
  // (access tokens expire at 15 min) rather than waiting for it to just
  // stop working silently, same pattern as the Dashboard's queue view.
  useEffect(() => {
    let es;
    let renewTimer;

    function connect() {
      const { accessToken } = getTokens();
      if (!accessToken) return;
      es = new EventSource(`/api/cases/${id}/stream?access_token=${encodeURIComponent(accessToken)}`);
      es.onmessage = (evt) => {
        try {
          const evtData = JSON.parse(evt.data);
          if (evtData.type === 'new_message') {
            load();
            if (evtData.role === 'user') markSeen();
          } else if (evtData.type === 'typing_user') {
            setUserTyping(true);
            clearTimeout(userTypingTimeout.current);
            userTypingTimeout.current = setTimeout(() => setUserTyping(false), 3000);
          }
        } catch { /* heartbeat/keepalive */ }
      };
      es.onerror = () => es.close();
    }

    connect();
    renewTimer = setInterval(() => { es?.close(); connect(); }, 10 * 60 * 1000);
    return () => { clearInterval(renewTimer); es?.close(); };
  }, [id, load, markSeen]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [data, userTyping]);

  function handleReplyInput(e) {
    setReply(e.target.value);
    if (typingTimeout.current) return;
    api(`/realtime/cases/${id}/typing`, { method: 'POST' }).catch(() => {});
    typingTimeout.current = setTimeout(() => { typingTimeout.current = null; }, 3000);
  }

  async function sendReply(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api(`/cases/${id}/reply`, { method: 'POST', body: JSON.stringify({ content: reply }) });
      setReply('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  async function changeStatus(toStatus, override = false) {
    try {
      await api(`/cases/${id}/status`, { method: 'POST', body: JSON.stringify({ status: toStatus, note: note || undefined, override }) });
      setNote('');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (error) return <div className="p-8 text-p1">{error}</div>;
  if (!data) return <div className="p-8 text-ink/50">Loading case…</div>;

  const nextOptions = NEXT_STATUS_OPTIONS[data.status] ?? [];

  return (
    <div className="flex h-screen">
      <div className="flex-1 flex flex-col border-r border-ink/10">
        <div className="px-6 py-4 border-b border-ink/10 flex items-center justify-between">
          <div>
            <button onClick={() => navigate(-1)} className="text-xs text-ink/50 hover:text-ink mb-1">&larr; Back to queue</button>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-ink/70">{data.case_ref}</span>
              <PriorityBadge priority={data.priority} />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" aria-live="polite">
          {data.messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}>
              <div
                className={`max-w-md rounded-lg px-3.5 py-2 text-sm ${
                  m.role === 'user' ? 'bg-white border border-ink/10' :
                  m.role === 'staff' ? 'bg-accent text-white' : 'bg-accent-100 text-accent-600'
                }`}
              >
                <div className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5">{m.role}</div>
                <MessageContent caseId={data.id} content={m.content} />
              </div>
            </div>
          ))}
          {userTyping && (
            <div className="flex justify-start">
              <div className="rounded-lg px-3.5 py-2.5 bg-white border border-ink/10">
                <span className="inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-ink/30 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-ink/30 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-ink/30 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={sendReply} className="px-6 py-4 border-t border-ink/10 flex gap-2">
          <input
            value={reply} onChange={handleReplyInput}
            placeholder="Type your reply…"
            className="flex-1 rounded-md border border-ink/20 px-3 py-2 text-sm focus:border-accent focus:ring-1 focus:ring-accent"
          />
          <button
            type="submit" disabled={sending || !reply.trim()}
            className="rounded-md bg-accent text-white text-sm font-medium px-4 py-2 hover:bg-accent-600 disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </div>

      <div className="w-80 shrink-0 p-6 space-y-6 overflow-y-auto">
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-2">Case status</h2>
          <div className="text-sm font-medium capitalize">{data.status.replace('_', ' ')}</div>
          {data.status_note && <p className="text-xs text-ink/60 mt-1">{data.status_note}</p>}
        </section>

        {nextOptions.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-2">Update status</h2>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note…"
              rows={2}
              className="w-full rounded-md border border-ink/20 px-2.5 py-1.5 text-sm mb-2 focus:border-accent focus:ring-1 focus:ring-accent"
            />
            <div className="flex flex-wrap gap-2">
              {nextOptions.map((s) => (
                <button
                  key={s} onClick={() => changeStatus(s)}
                  className="rounded-md border border-ink/20 px-3 py-1.5 text-xs font-medium hover:bg-ink hover:text-white transition-colors capitalize"
                >
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-2">Category</h2>
          <div className="text-sm capitalize">{data.category?.replace('_', ' ') ?? '—'}</div>
        </section>

        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-2">Assigned to</h2>
          <div className="text-sm">{data.assigned_staff_name ?? 'Unassigned'}</div>
        </section>
      </div>
    </div>
  );
}
